/**
 * PresenceAvatars - Real-time Collaboration Header Component
 * 
 * Shows a stack of avatars for users currently viewing the same page.
 * Features:
 * - Avatar stack with overflow indicator (+N)
 * - Tooltip with full user list
 * - Active (green) / Idle (yellow) status indicators
 * - Smooth animations for users joining/leaving
 * - Click on user to start DM chat
 */

import React, { useState } from "react";
import { usePresence, getInitials } from "../../context/PresenceContext";

// Format last active time
function formatLastActive(timestamp) {
  if (!timestamp) return 'Unknown';
  const now = new Date();
  const last = new Date(timestamp);
  const diff = now - last;
  
  if (diff < 60000) return 'Active now';
  if (diff < 3600000) return `Active ${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `Active ${Math.floor(diff / 3600000)}h ago`;
  return `Active ${Math.floor(diff / 86400000)}d ago`;
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function PresenceAvatars({ maxVisible = 3 }) {
  const { users, isEnabled } = usePresence();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, right: 0 });
  const containerRef = React.useRef(null);
  
  if (!isEnabled || users.length === 0) {
    return null;
  }
  
  const visibleUsers = users.slice(0, maxVisible);
  const overflowCount = Math.max(0, users.length - maxVisible);
  
  const handleMouseEnter = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setTooltipPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setShowTooltip(true);
  };

  // Start DM with user
  const handleUserClick = (user) => {
    console.log('[PresenceAvatars] User clicked:', user);
    console.log('[PresenceAvatars] authUserId:', user.authUserId);
    // Dispatch custom event that ChatWidget listens for
    window.dispatchEvent(new CustomEvent('venueflow:startDM', { 
      detail: { 
        authUserId: user.authUserId,
        userName: user.name,
        userEmail: user.email,
        userAvatar: user.avatar,
        isOnline: user.status === 'active',
        lastActive: user.lastActive,
      } 
    }));
    setShowTooltip(false);
  };
  
  return (
    <div 
      ref={containerRef}
      style={styles.container}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Avatar Stack */}
      <div style={styles.avatarStack}>
        {visibleUsers.map((user, index) => (
          <Avatar 
            key={user.id} 
            user={user} 
            index={index}
            total={visibleUsers.length}
            onClick={() => handleUserClick(user)}
          />
        ))}
        
        {/* Overflow indicator */}
        {overflowCount > 0 && (
          <div 
            style={{
              ...styles.avatar,
              ...styles.overflowBadge,
              zIndex: visibleUsers.length,
              marginLeft: visibleUsers.length > 0 ? -8 : 0,
            }}
          >
            +{overflowCount}
          </div>
        )}
      </div>
      
      {/* "viewing" label */}
      <span style={styles.viewingLabel}>
        {users.length === 1 ? '1 viewing' : `${users.length} viewing`}
      </span>
      
      {/* Tooltip - fixed position to avoid clipping */}
      {showTooltip && (
        <div style={{
          ...styles.tooltip,
          top: tooltipPos.top,
          right: tooltipPos.right,
        }}>
          <div style={styles.tooltipTitle}>Currently viewing • Click to chat</div>
          {users.map((user) => (
            <div 
              key={user.id} 
              style={styles.tooltipUser}
              onClick={() => handleUserClick(user)}
            >
              <div style={styles.tooltipAvatarWrapper}>
                {user.avatar ? (
                  <img 
                    src={user.avatar} 
                    alt={user.name}
                    style={styles.tooltipAvatar}
                  />
                ) : (
                  <div 
                    style={{
                      ...styles.tooltipAvatar,
                      ...styles.tooltipInitials,
                      background: user.color,
                    }}
                  >
                    {getInitials(user.name)}
                  </div>
                )}
                <div 
                  style={{
                    ...styles.statusDot,
                    background: user.status === 'active' ? '#22C55E' : '#F59E0B',
                  }}
                />
              </div>
              <div style={styles.tooltipUserInfo}>
                <div style={styles.tooltipUserName}>{user.name}</div>
                <div style={styles.tooltipUserStatus}>
                  {user.status === 'active' ? 'Active now' : formatLastActive(user.lastActive)}
                  {user.editingRow && (
                    <span style={styles.editingBadge}> • Editing</span>
                  )}
                </div>
              </div>
              <div style={styles.chatIcon}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2fd6b5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// AVATAR COMPONENT
// ============================================================
function Avatar({ user, index, total, onClick }) {
  const [imageError, setImageError] = useState(false);
  
  const showImage = user.avatar && !imageError;
  
  return (
    <div 
      style={{
        ...styles.avatar,
        zIndex: total - index,
        marginLeft: index > 0 ? -8 : 0,
        border: `2px solid ${user.status === 'active' ? '#22C55E' : '#F59E0B'}`,
        cursor: 'pointer',
      }}
      title={`Chat with ${user.name}`}
      onClick={onClick}
    >
      {showImage ? (
        <img 
          src={user.avatar} 
          alt={user.name}
          style={styles.avatarImage}
          onError={() => setImageError(true)}
        />
      ) : (
        <div 
          style={{
            ...styles.avatarInitials,
            background: user.color,
          }}
        >
          {getInitials(user.name)}
        </div>
      )}
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    position: 'relative',
  },
  avatarStack: {
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    overflow: 'hidden',
    position: 'relative',
    transition: 'transform 0.2s ease, margin 0.2s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  avatarInitials: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
  },
  overflowBadge: {
    background: '#374151',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 600,
    border: '2px solid #1f2937',
  },
  viewingLabel: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.6)',
    fontWeight: 500,
  },
  tooltip: {
    position: 'fixed',
    background: '#1f2937',
    borderRadius: 12,
    padding: 12,
    minWidth: 200,
    boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
    zIndex: 9999,
    animation: 'fadeIn 0.15s ease',
  },
  tooltipTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 10,
  },
  tooltipUser: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 0',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    cursor: 'pointer',
    borderRadius: 6,
    transition: 'background 0.15s ease',
  },
  tooltipAvatarWrapper: {
    position: 'relative',
    flexShrink: 0,
  },
  tooltipAvatar: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    objectFit: 'cover',
  },
  tooltipInitials: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 600,
    color: '#fff',
  },
  statusDot: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 8,
    height: 8,
    borderRadius: '50%',
    border: '2px solid #1f2937',
  },
  tooltipUserInfo: {
    flex: 1,
    minWidth: 0,
  },
  tooltipUserName: {
    fontSize: 13,
    fontWeight: 500,
    color: '#fff',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  tooltipUserStatus: {
    fontSize: 11,
    color: '#9CA3AF',
  },
  editingBadge: {
    color: '#2fd6b5',
  },
  chatIcon: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

// Add keyframes for tooltip animation
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(-4px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(styleSheet);
}
